import { useClientVD } from '@solinkcorp/agent/hooks/useClientVD';
import { honoFetcher } from '@solinkcorp/agent/lib/honoFetcher';
import { useMemo } from 'react';
import useSWR from 'swr';
import type { ClientVD, InferOutput } from 'vd-server/client';

export type Investigation = InferOutput<ClientVD['investigations']['$get']>[number];
export type InvestigationStatus = Investigation['status'];
export type InvestigationVisibility = Investigation['visibility'];
export type InvestigationEvent = Investigation['events'][number];
export type InvestigationEventKind = InvestigationEvent['kind'];

export function useInvestigations(status?: InvestigationStatus) {
  const client = useClientVD();

  const { data, error, isLoading, mutate } = useSWR(
    ['investigations', status ?? null],
    async () => honoFetcher(client.investigations.$get({ query: status ? { status } : {} })),
    { revalidateOnFocus: false },
  );

  return { investigations: data, isLoading, error, mutate };
}

export function useInvestigationCounts() {
  const { investigations } = useInvestigations();

  return useMemo(() => {
    if (!investigations) return { open: 0, archived: 0 };
    return investigations.reduce(
      (acc, inv) => {
        acc[inv.status] = (acc[inv.status] ?? 0) + 1;
        return acc;
      },
      { open: 0, archived: 0 } as Record<InvestigationStatus, number>,
    );
  }, [investigations]);
}
